import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useSubscription } from '@/hooks/useSubscription';

interface BusinessRouteProps {
  children: React.ReactNode;
}

type DashboardAccess = 'member' | 'technician' | 'none';

const BusinessRoute = ({ children }: BusinessRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [access, setAccess] = useState<DashboardAccess | null>(null);
  const [suspended, setSuspended] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  // Card-required gate: signup pushes new owners straight to Stripe Checkout
  // and we don't grant a free in-DB trial anymore, so an account is only
  // "subscribed" once a card-backed (trialing or active) Stripe subscription
  // exists. Gate the fetch on auth-readiness to avoid caching a pre-session 401.
  const {
    subscribed,
    status: subStatus,
    loading: subLoading,
    error: subError,
  } = useSubscription({ enabled: !!user && !authLoading });

  useEffect(() => {
    const checkAccess = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Business-level suspension (set from admin.workloop.us). The binding
        // enforcement is RLS — this check only redirects to a readable /suspended
        // page instead of a broken empty dashboard. Fail OPEN: if the RPC errors
        // we treat the account as active, matching the subError fail-open below,
        // because the RLS layer still cuts data server-side regardless.
        try {
          const { data: susp, error: suspErr } = await supabase
            .rpc('my_business_suspension')
            .maybeSingle();
          if (!suspErr && susp?.suspended === true) {
            setSuspended(true);
          }
        } catch (e) {
          console.warn('suspension check failed (fail-open):', e);
        }

        // An active business_users membership is the authoritative dashboard
        // grant; technicians resolve via technicians.user_id. profiles.role was
        // retired as an authorization source (roles consolidation, projects#172).
        const { data: membership } = await supabase
          .from('business_users')
          .select('id')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .limit(1)
          .maybeSingle();

        if (membership) {
          setAccess('member');
          return;
        }

        const { data: technician } = await supabase
          .from('technicians')
          .select('id')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .limit(1)
          .maybeSingle();

        if (technician) {
          setAccess('technician');
          return;
        }

        setAccess('none');
      } catch (error) {
        console.error('Error checking dashboard access:', error);
        setAccess('none');
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      checkAccess();
    }
    // Key on the stable user id, not the user object, so a token refresh /
    // tab-focus re-emit doesn't re-query the role and transiently bounce to
    // /auth (the old /auth<->/dashboard ping-pong).
  }, [user?.id, authLoading]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    // Remember where the user was headed so Auth can send them back after
    // sign-in instead of always dumping them on /dashboard.
    return <Navigate to="/auth" replace state={{ from: location }} />;
  }

  // Suspended businesses are cut off entirely (RLS enforces it); send them to a
  // page that explains why instead of an empty dashboard.
  if (suspended) {
    return <Navigate to="/suspended" replace />;
  }

  // Only active business members (owner/admin/member) may enter the dashboard
  if (access !== 'member') {
    // Redirect technicians to a special page explaining mobile app usage
    if (access === 'technician') {
      return <Navigate to="/technician-info" replace />;
    }

    return <Navigate to="/auth" replace />;
  }

  // Subscription paywall. The billing page is always reachable (that's where
  // the user completes Checkout) and so is /onboarding (a legitimate
  // pre-subscription step with no gated features) — otherwise an unsubscribed
  // account is sent to billing. Exempting /onboarding is what breaks the
  // /onboarding ↔ /dashboard/billing redirect loop: without it, an
  // unsubscribed + not-onboarded user bounces here off the wizard while
  // DashboardLayout bounces them off billing, freezing the screen. Fail OPEN
  // on a transient check-subscription error — the edge functions still enforce
  // limits server-side, so a flaky check shouldn't hard-lock a paying customer
  // out of their dashboard.
  const onBillingPage = location.pathname.endsWith('/billing');
  const onOnboardingPage = location.pathname === '/onboarding';
  if (!onBillingPage && !onOnboardingPage) {
    if (subLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }
    if (!subError && !subscribed) {
      // A dunning account (failed renewal: past_due/unpaid/incomplete) is also
      // unsubscribed, but it must NOT see the "add a card to START your trial"
      // paywall toast — that's for brand-new accounts. Drop the
      // ?subscription=required param so Billing shows the dunning banner (driven
      // by status) with its "update your card" portal CTA instead.
      const isDunning =
        subStatus === 'past_due' || subStatus === 'unpaid' || subStatus === 'incomplete';
      return (
        <Navigate
          to={isDunning ? '/dashboard/billing' : '/dashboard/billing?subscription=required'}
          replace
        />
      );
    }
  }

  return <>{children}</>;
};

export default BusinessRoute;